import { useToasts } from "@/context/toasts";
import { API_STATUS_BAD_REQUEST, API_STATUS_CONTENT_TOO_LARGE, createApiClient } from "@/lib/api";
import React, { createContext, useContext, useMemo } from "react";

type ContextState = {
  client: ReturnType<typeof createApiClient>;
};

const ApiContext = createContext<ContextState | undefined>(undefined);

/**
 * Make a Hono client available when calling useApi().
 * This client shows a warning for a few errors so we don't need to do this
 * everywhere we call the client in the browser.
 */
export function ApiProvider({ children }: { children: React.ReactNode }) {
  const { addToast } = useToasts();

  const memoizedValue = useMemo(() => {
    const client = createApiClient({
      onError: (options) => {
        if (options.type === "response") {
          const { response } = options;

          // Don't show the generic error toast for 400 Bad Request
          if (response.status === API_STATUS_BAD_REQUEST) {
            return;
          }

          // Don't show the generic error toast for 413 Content Too Large
          if (response.status === API_STATUS_CONTENT_TOO_LARGE) {
            return;
          }
        }

        addToast({ type: "error", message: "Noe gikk galt" });
      },
    });

    return {
      client,
    };
  }, [addToast]);

  return <ApiContext.Provider value={memoizedValue}>{children}</ApiContext.Provider>;
}

export function useApi() {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error("useApi must be used within an ApiProvider");
  }

  return context;
}
